/**
 * Core game constants and configuration values.
 * Ported from the Phaser codebase and adapted to 3D where needed.
 *
 * In the Babylon.js version:
 * - Positions use (X=right, Y=up, Z=depth). Camera looks from +Z toward origin.
 * - Physics gravity is in world units/s^2 (Havok default scale).
 * - Pixel-based speeds from Phaser are converted to approximate world-unit equivalents
 *   (1 Phaser px ~= 0.05 Babylon units for our scale).
 */

/** Canvas / viewport dimensions (used for HTML sizing, not Babylon internal resolution). */
export const GAME_WIDTH = 1024;
export const GAME_HEIGHT = 768;

/** Reference width for GUI scaling — all AdvancedDynamicTexture controls scale relative to this. */
export const GUI_IDEAL_WIDTH = 1920;

/** Physics tuning — Havok uses real-world-ish units (meters, seconds). */
export const PHYSICS = {
  GRAVITY_Y: -15,         // Faster than real gravity (-9.81) for snappier platformer feel
  DEBUG: true,
} as const;

/**
 * Runtime world debug toggles.
 * Keep these off by default so trigger/collider helpers do not read as finished level art.
 */
export const WORLD_DEBUG = {
  SHOW_SHADOW_ZONE_VISUALS: false,
} as const;

/**
 * Player movement tuning.
 * Adapted from Phaser px/s values: divide by ~50 to get Babylon world units/s.
 */
export const PLAYER = {
  MOVE_SPEED: 5,           // World units per second (was 220 px/s in Phaser)
  JUMP_IMPULSE: 9,         // Upward impulse magnitude. At gravity -15 this gives a
                           // peak of ~2.7 units above feet — enough to clear
                           // Liminal Atrium's low platforms (tops at y 1.7, 2.2)
                           // from ground while still requiring Overclocked Leap
                           // for the higher platforms (3.2, 3.7, 4.2).
  ACCELERATION: 25,        // Ground acceleration (world units/s^2)
  DECELERATION: 35,        // Ground deceleration
  AIR_ACCELERATION: 18,    // Reduced control in air
  AIR_DECELERATION: 10,    // Slow air deceleration (floatier)
  MAX_FALL_SPEED: 15,      // Terminal velocity (world units/s)
  COYOTE_TIME: 0.12,       // Seconds after leaving ground that jump still works
  JUMP_BUFFER_TIME: 0.15,  // Seconds a pressed jump is buffered before landing
  CAPSULE_HEIGHT: 1.4,     // Player capsule total height
  CAPSULE_RADIUS: 0.35,    // Player capsule radius
  JUMP_CUT_MULTIPLIER: 0.45,  // Scale factor for jump height when button released early
  // FALL_MULTIPLIER: factor applied to |GRAVITY_Y| to derive extra downward
  // acceleration during descent (fast-fall). Formula: extra accel = |GRAVITY_Y| * (FALL_MULTIPLIER - 1).
  // At 1.0, no fast-fall; at 2.0, fall speed doubles per FALL_MULTIPLIER seconds.
  FALL_MULTIPLIER: 2.0,

  // ── Shared Camera — max horizontal separation before the frame
  //    "gently restricts movement" per 2026-04-14 companion book
  //    canon. `MAX_SEPARATION` is the horizontal distance at which
  //    the outward-moving cat starts being damped; `SEPARATION_SOFT_BAND`
  //    is the additional distance over which the damping ramps from
  //    full speed to zero outward velocity. Total hard cap =
  //    MAX_SEPARATION + SEPARATION_SOFT_BAND.
  MAX_SEPARATION: 12,
  SEPARATION_SOFT_BAND: 3,
} as const;

/** Color palette — hex strings for Babylon Color3.FromHexString(). */
export const COLORS = {
  DEEP_PURPLE: '#2a1f3d',
  MIDNIGHT_BLUE: '#0f1b2d',
  EMERALD: '#00cc88',
  BRASS: '#c49b3c',
  COPPER: '#b87333',
  BIOLUMINESCENT_GREEN: '#00ff88',
  BIOLUMINESCENT_BLUE: '#00ccff',
  BIOLUMINESCENT_PURPLE: '#7700ff',
  MINNA_AURA: '#00ff88',
  BASIL_HARNESS: '#c49b3c',
  GROUND: '#3a3040',
  PLATFORM: '#5a4030',
  SKY_TOP: '#0a0520',
  SKY_BOTTOM: '#1a0f2d',
} as const;

/**
 * Per-level audio configuration.
 * Maps scene keys to ambient track filenames (in assets/audio/).
 * AudioManager reads this to select the correct ambient loop per level.
 */
export const LEVEL_AUDIO: Record<string, { ambient: string }> = {
  LiminalAtrium: { ambient: 'ambient-atrium.wav' },
  RedwoodScrapyard: { ambient: 'ambient-scrapyard.wav' },
  OvergrownConservatory: { ambient: 'ambient-conservatory.wav' },
  ClockworkCanopy: { ambient: 'ambient-canopy.wav' },
} as const;

/**
 * Post-processing and bloom pipeline tuning per level atmosphere.
 * Engine.ts / BaseLevel.ts apply these to DefaultRenderingPipeline.
 */
export const POST_PROCESSING = {
  DEFAULT: {
    bloomEnabled: true,
    bloomWeight: 0.35,
    bloomThreshold: 0.7,
    bloomKernel: 64,
    chromaticAberrationEnabled: true,
    chromaticAmount: 15,
  },
  LiminalAtrium: {
    bloomEnabled: true,
    bloomWeight: 0.4,
    bloomThreshold: 0.65,
    bloomKernel: 64,
    chromaticAberrationEnabled: true,
    chromaticAmount: 12,
  },
  RedwoodScrapyard: {
    bloomEnabled: true,
    bloomWeight: 0.3,
    bloomThreshold: 0.75,
    bloomKernel: 64,
    chromaticAberrationEnabled: true,
    chromaticAmount: 18,
  },
  OvergrownConservatory: {
    bloomEnabled: true,
    bloomWeight: 0.5,
    bloomThreshold: 0.6,
    bloomKernel: 64,
    chromaticAberrationEnabled: true,
    chromaticAmount: 22,
  },
  ClockworkCanopy: {
    bloomEnabled: true,
    bloomWeight: 0.45,
    bloomThreshold: 0.65,
    bloomKernel: 64,
    chromaticAberrationEnabled: true,
    chromaticAmount: 20,
  },
} as const;


/**
 * Energy system tuning.
 * Kinetic Battery (Basil) and Ethereal Focus (Minna).
 */
export const ENERGY = {
  MAX: 100,
  DEPLETED_THRESHOLD: 10,

  // ── Basil: Kinetic Battery ──
  BASIL: {
    PASSIVE_RECHARGE: 3,          // energy/second when idle
    NPC_INTERACTION_BURST: 20,    // on FOOD_DELIVERED or NPC_CALMED
    DEPLETED_SPEED_SCALE: 0.4,   // 40% movement when depleted
    /** Drain costs per ability use. */
    DRAIN: {
      OVERCLOCKED_LEAP: 25,
      PNEUMATIC_ANCHOR_PER_SEC: 5,
      RESONANT_DROP: 35,
    },
  },

  // ── Minna: Ethereal Focus ──
  MINNA: {
    PASSIVE_RECHARGE: 5,          // energy/second in shadow zones
    COREGULATION_BONUS: 3,        // +3/sec when near Basil
    DEPLETED_CALMING_RANGE_SCALE: 0.5,
    DEPLETED_SHADOW_FAIL_CHANCE: 0.3, // 30% per second
    /** Drain rates (per second unless noted). */
    DRAIN: {
      NEAR_FOX_HUNTING: 8,
      NEAR_CROW_ALERT: 5,
      AURA_SIGHT: 3,
      SHADOW_SLIP: 4,
    },
  },

  // ── Co-Regulation ──
  COREGULATION: {
    DISTANCE: 3,                  // world units — max distance between cats
    BONUS_PER_SEC: 3,            // energy/second each cat gains
  },
} as const;

/**
 * Harness Wound tuning — Basil only. Composes WITH `ENERGY.BASIL`.
 *
 * Thresholds match `SPEC-wounds-and-medicine.md` §2:
 *   intact  : 76–100
 *   cracked : 41–75
 *   venting : 11–40
 *   limping :  0–10
 *
 * Per-state ability multipliers from spec §3.
 */
export const HARNESS = {
  MAX: 100,
  THRESHOLDS: {
    INTACT_MIN: 76,
    CRACKED_MIN: 41,
    VENTING_MIN: 11,
    LIMPING_MIN: 0,
  },

  /** Damage amounts per source. */
  DAMAGE: {
    FALL_HEAVY: 10,           // landing from >2× fall speed
    NPC_HIT: 25,              // fox bite
    FAILED_RHYTHM_VENTING: 4, // missed TTT hit while already venting
    STEAM_VENT: 15,
    SCRIPTED_APEX: 20,        // generic hook for scripted events
  },

  /** Y threshold under which a fall lands as "heavy" (>2× fall speed). */
  FALL_DAMAGE_SPEED_THRESHOLD: 20, // abs(vy) > 20 → heavy-fall damage

  /** Healing sources. */
  HEAL: {
    PURR_PER_SEC: 8,          // while Minna's Purr is active + within radius
    GREASE_CACHE: 25,         // per-pickup burst
    PASSIVE_IDLE_PER_SEC: 1,  // after 3s without damage
    ELDER_FULL: 100,          // Elder cutscenes restore to Intact
  },

  /** Seconds after last damage before passive regen kicks in. */
  PASSIVE_REGEN_DELAY: 3,

  /** Grease Cache respawn seconds (Hollow-Knight-bench cadence). */
  GREASE_CACHE_RESPAWN: 30,

  /** Basil must be within this distance (world units) to trigger a Grease Cache. */
  GREASE_CACHE_INTERACT_RADIUS: 1.8,

  /** Per-state tuning (multipliers relative to Intact baselines). */
  STATES: {
    INTACT: {
      moveSpeedScale: 1.0,
      ttt: { halfWidthRad: 0.4, markerSpeed: 2.5, comboMult: 0.15 },
      anchor: { drainScale: 1.0, cooldownScale: 1.0, maxHoldSec: Infinity, lockedOut: false },
      leap: { drainScale: 1.0, cooldownScale: 1.0, lockedOut: false },
      shockwave: { drainScale: 1.0, cooldownScale: 1.0, lockedOut: false },
    },
    CRACKED: {
      moveSpeedScale: 0.95,
      ttt: { halfWidthRad: 0.55, markerSpeed: 2.2, comboMult: 0.15 },
      anchor: { drainScale: 1.2, cooldownScale: 1.33, maxHoldSec: Infinity, lockedOut: false },
      leap: { drainScale: 1.12, cooldownScale: 1.125, lockedOut: false },
      shockwave: { drainScale: 1.1, cooldownScale: 1.1, lockedOut: false },
    },
    VENTING: {
      moveSpeedScale: 0.85,
      ttt: { halfWidthRad: 0.8, markerSpeed: 1.75, comboMult: 0.25 },
      anchor: { drainScale: 1.6, cooldownScale: 2.0, maxHoldSec: 4, lockedOut: false },
      leap: { drainScale: 1.4, cooldownScale: 1.5, lockedOut: false },
      shockwave: { drainScale: 1.27, cooldownScale: 1.4, lockedOut: false },
    },
    LIMPING: {
      moveSpeedScale: 0.70,
      ttt: { halfWidthRad: 1.0, markerSpeed: 1.4, comboMult: 0.30 },
      anchor: { drainScale: 1.0, cooldownScale: 1.0, maxHoldSec: 0, lockedOut: true },
      leap: { drainScale: 1.0, cooldownScale: 1.0, lockedOut: true },
      shockwave: { drainScale: 1.0, cooldownScale: 1.0, lockedOut: true },
    },
  },
} as const;

/**
 * Fall-damage bands — Phase 5 flow kernel (I1: net-drop model).
 *
 * Metric: NET DROP distance = max(0, lastGroundedY − landingY).
 * A normal jump or OverclockedLeap that returns to the same Y nets ~0 drop
 * and produces 'none', regardless of fast-fall or boost speed. Only genuine
 * falls off ledges accumulate a meaningful drop distance.
 *
 * Thresholds (world units of drop):
 *   netDrop <  4        -> 'none'  (normal landing; no damage, no shake)
 *   4 <= netDrop < 7    -> 'light' (Basil: 6 harness; Minna: shake feedback)
 *   netDrop >= 7        -> 'heavy' (Basil: 14 harness; Minna: shake feedback)
 *
 * Chosen margins: a flat jump nets ~0 drop; platform-to-platform drops of
 * 1-3 wu are safe; a meaningful ledge fall is 4+ wu (light) or 7+ wu (heavy).
 *
 * Basil (harness) takes integrity damage; Minna (no harness) gets camera
 * shake + the existing land.wav SFX only — never integrity loss.
 */
export const FALL_DAMAGE = {
  LIGHT_THRESHOLD: 4,           // net-drop wu: below this is a safe landing
  HEAVY_THRESHOLD: 7,           // net-drop wu: at/above this is a hard fall
  LIGHT_DAMAGE: 6,              // Intact 100 -> 94: stays Intact (forgiving)
  HEAVY_DAMAGE: 14,             // Intact 100 -> 86... repeated heavies feed Task 7's harness-0 fail
  MINNA_SHAKE_INTENSITY: 0.25,  // world units, additive (CameraSystem.triggerShake)
  MINNA_SHAKE_MS: 220,          // duration hint (informational; decay is SHAKE_DECAY_PER_SEC)
} as const;

/** Harness state discriminated union shared by the system + HUD. */
export type HarnessState = "intact" | "cracked" | "venting" | "limping";

/** Scene identifiers. */
export const SCENE_KEYS = {
  BOOT: 'BootScene',
  PRELOAD: 'PreloadScene',
  MENU: 'MenuScene',
  LIMINAL_ATRIUM: 'LiminalAtrium',
  REDWOOD_SCRAPYARD: 'RedwoodScrapyard',
  OVERGROWN_CONSERVATORY: 'OvergrownConservatory',
  CLOCKWORK_CANOPY: 'ClockworkCanopy',
  UI: 'UIScene',
} as const;

/**
 * Screen orientation of the world X axis.
 *
 * The camera sits at +Z looking toward -Z, which in Babylon's left-handed
 * system renders world +X on the LEFT of the screen, so every level ran
 * right-to-left and designer text written in world terms ("walk right to
 * the crane") pointed players off the level's start edge. With MIRROR_X the
 * main camera's projection is mirrored (CameraSystem) so world +X is
 * screen-RIGHT and levels read left-to-right like most side-scrollers
 * (Unravel included). Movement input follows: "right" always means the
 * direction that looks right on screen. Art and colliders stay aligned
 * because the whole image is mirrored; text baked into textures reads
 * mirrored, which is the known cost.
 */
export const VIEW = {
  MIRROR_X: true,
} as const;

/** +1 when pressing "right" should move toward world +X. */
export const SCREEN_RIGHT_WORLD_SIGN: 1 | -1 = VIEW.MIRROR_X ? 1 : -1;

/** Camera tuning. */
export const CAMERA = {
  /** Z distance from the action plane (players move at Z=0). Floor for
   *  the span-aware zoom — the camera will never pull closer than this. */
  Z_DISTANCE: 9,
  /** Ceiling for the span-aware zoom. Beyond this cats stay off-screen
   *  rather than shrinking into a meaningless wide shot. */
  MAX_Z_DISTANCE: 22,
  /** World-unit padding added to the inter-cat span when computing the
   *  required Z so both cats stay comfortably inside the frame edges. */
  SPAN_PADDING: 4,
  /**
   * Frame-rate-independent follow smoothing time-constant (1/seconds).
   * k=6 gives alpha ≈ 0.095 at 60fps.
   * Increase for a tighter follow; decrease for floatier.
   */
  LERP_K: 6,
  /**
   * Vertical follow is deliberately slower than horizontal so jumps
   * don't yank the frame upward. k=3 at 60fps gives alpha ≈ 0.049.
   */
  VERTICAL_LERP_K: 3,
  /**
   * Separate slower time-constant for Z so zoom changes feel smooth
   * when cats momentarily separate on a jump arc. k=2.5 at 60fps ≈ 0.040.
   */
  ZOOM_LERP_K: 2.5,
  /** Vertical offset above midpoint of players. */
  Y_OFFSET: 1.2,
  /** Field of view in radians (narrower = more zoom). */
  FOV: 0.9,

  // ── Lookahead ──────────────────────────────────────────────────────────
  /** Scale applied to average horizontal velocity for the lookahead offset.
   *  0.18 at walk speed 5 gives ~0.9 world units ahead — enough to preview
   *  the next platform without feeling too aggressive. */
  LOOKAHEAD_SCALE: 0.18,
  /** Maximum lookahead offset (world units). Caps runaway lookahead at sprint. */
  LOOKAHEAD_MAX: 1.5,

  // ── Deadzones ─────────────────────────────────────────────────────────
  /** X deadzone: camera won't move horizontally unless target shifts more
   *  than this (world units). Prevents micro-jitter from standing still. */
  DEADZONE_X: 0.15,
  /** Y deadzone: vertical deadzone is larger so minor hops don't pull the
   *  frame. Value tuned so a small jump arc (peak ~0.8 units) is ignored. */
  DEADZONE_Y: 0.4,

  // ── Additive screen-shake ──────────────────────────────────────────────
  /** Linear decay rate of shake magnitude (world units per second).
   *  At 5.0 a strong shake (intensity 1.0) fades in ~0.2 s. */
  SHAKE_DECAY_PER_SEC: 5.0,

  // ── Tab refocus ───────────────────────────────────────────────────────
  /** How long (seconds) the focus bias toward the newly-active cat lasts
   *  after a Tab character switch. */
  SWITCH_FOCUS_DURATION: 0.6,
  /** Peak focus weight toward the switched-to cat (0=midpoint only, 1=cat only). */
  SWITCH_FOCUS_WEIGHT: 0.35,

  // ── Ambient particle culling ──────────────────────────────────────────
  /**
   * Camera Z distance (world units) above which ambient particle emitters
   * are culled (emitRate → 0). Camera Z spans [Z_DISTANCE=9 (solo close) ..
   * MAX_Z_DISTANCE=22 (max co-op separation)]. Threshold 20 fires ONLY in
   * wide co-op zoom-out shots (z ∈ [20, 22]) and never during normal close
   * play (z ~ 9–15) — ambient motes are too small at z ≥ 20 to be visible.
   * GATED OFF under QA (window.__qa present) so the headless navigator
   * never sees emit-rate mutations that could affect sampled timing.
   */
  AMBIENT_CULL_DISTANCE: 20,
  /**
   * Hysteresis dead-band (world units). The camera must pull back below
   * AMBIENT_CULL_DISTANCE - AMBIENT_CULL_HYSTERESIS before emit is restored.
   * Prevents rapid on/off flapping when the camera hovers near the threshold.
   */
  AMBIENT_CULL_HYSTERESIS: 2,
} as const;

/** World dimensions for the Liminal Atrium test level. */
export const ATRIUM = {
  GROUND_WIDTH: 40,
  GROUND_DEPTH: 2,

  /**
   * Per-ability tuning overrides for Level 0 (tutorial).
   * Matches the per-level puzzle tuning pattern used by SCRAPYARD.PUZZLE
   * and MIDNIGHT_BURROW.PUZZLE — values here are fed into ability
   * constructors via BaseLevelConfig so later levels keep their defaults.
   */
  ABILITY_TUNING: {
    /**
     * Tutorial-easy Turn-Table Touch: 2× hit window (1.6 rad total),
     * 0.7× marker speed (1.75 rad/s), combo speedup disabled. Gives the
     * player a generous first contact with Basil's rhythm mechanic
     * without blunting the challenge on Levels 1-3.
     */
    TURN_TABLE_TOUCH: {
      markerSpeedRadPerSec: 1.75,
      hitZoneHalfWidthRad: 0.8,
      comboSpeedMultiplier: 0,
    },
  },
} as const;

/**
 * Redwood Scrapyard — Level 1 configuration.
 * World dimensions, puzzle parameters, and scrapyard-specific colors.
 * Phaser pixel values converted: 1 Phaser px ~= 0.05 Babylon units.
 */
export const SCRAPYARD = {
  /** World bounds in Babylon units (Phaser: 3072x1024 px). */
  WORLD_WIDTH: 154,    // 3072 / 20
  WORLD_HEIGHT: 51,    // 1024 / 20
  GROUND_DEPTH: 2,

  /** Scrapyard-specific colors. */
  COLORS: {
    SKY_TOP: '#0a0818',
    SKY_BOTTOM: '#1a1228',
    REDWOOD_DARK: '#2b1510',
    REDWOOD_BARK: '#4a2a1a',
    MOSS: '#2a4a2a',
    RUST: '#8b4513',
  },

  /** Puzzle tuning. */
  PUZZLE: {
    CRANE_NOTCHES: 6,
    CRANE_TARGET: 4,
    FOOD_ITEMS_NEEDED: 2,
    KITTENS_TO_CALM: 3,
    JIGSAW_PIECES: 6,
    RESONANT_DROP_RANGE: 5.0,       // Max distance between cats for activation
    RESONANT_DROP_RADIUS: 12.5,     // Shockwave max radius (250 px / 20)
    RESONANT_DROP_COOLDOWN: 3.0,    // Seconds
  },

  /** NPC spawn tuning. */
  NPC: {
    // The thieving squirrel scurries about the foot of the crow's tree
    // (nest at x≈66.8), within Minna's purr range of the leap lane.
    SQUIRREL_PATROL_MIN: 61,
    SQUIRREL_PATROL_MAX: 65,
  },
} as const;

/**
 * Overgrown Conservatory level configuration.
 * Ported from Phaser and adapted to Babylon world units.
 */
export const OVERGROWN_CONSERVATORY = {
  /** World size in Babylon units (Phaser was 3200x2048 px; /20 = 160x102). */
  WORLD_WIDTH: 160,
  WORLD_HEIGHT: 102,
  GROUND_DEPTH: 2,

  /** Ambient color palette (hex strings for Color3.FromHexString). */
  COLORS: {
    AMBER_WARM: '#8b6914',
    KITCHEN_COPPER: '#b87333',
    BURROW_EARTH: '#3b2a1a',
    BURROW_DEEP: '#1a0f08',
    MIDNIGHT_INDIGO: '#0a0520',
    MIDNIGHT_DEEP: '#050210',
  },

  /**
   * Lighting per phase — canonical Incubation/Irrigation flip (day=
   * irrigation cool mist, midnight=incubation hot amber).
   *
   * Per 2026-04-17 Gemini 3.1-pro fluidity review: the prior values
   * produced "only a minor tint change from dark grey to dark green"
   * on the phase flip; the book's brief calls for "blinding light,
   * oppressive heat" vs "cooling shadows, thick mist." The new values
   * widen the delta substantially:
   *   DAY (irrigation): cool teal-green ambient, paler cool key,
   *     intensity 1.2 (unchanged).
   *   MIDNIGHT (incubation): hot amber ambient, blown-out warm key,
   *     intensity 2.0 (bumped from 0.3 — incubation is BRIGHT, not
   *     dark, because the book's "blinding light" is key imagery).
   *   DAWN / DUSK intermediate values follow the same contrast arc.
   */
  LIGHTING: {
    DAWN:     { ambient: '#856a4a', directional: '#ffccaa', intensity: 1.0 },
    DAY:      { ambient: '#5a7a72', directional: '#c6e0dc', intensity: 1.2 },
    DUSK:     { ambient: '#b56a30', directional: '#ff9040', intensity: 1.6 },
    MIDNIGHT: { ambient: '#d89050', directional: '#ffb870', intensity: 2.0 },
  },

  NPC: {
    FOX_PATROL_SPEED: 6.0,
    FOX_ALERT_RADIUS: 9.0,
    FOX_CHASE_SPEED: 10.0,
    FOX_CALM_THRESHOLD: 100,
    OPOSSUM_FOLLOW_SPEED: 4.5,
    OPOSSUM_FOLLOW_DISTANCE: 2.5,
    OPOSSUM_SCATTER_SPEED: 8.0,
  },

  PUZZLE: {
    BAKING_STAGES: 3,
    BAKING_NOTCHES: [4, 6, 8] as readonly number[],
    BAKING_TARGETS: [2, 4, 6] as readonly number[],
    OPOSSUMS_TO_ESCORT: 4,
    FOXES_ACTIVE_AT_NIGHT: 3,
    CHRONOS_BUBBLE_RANGE: 7.5,      // 150 px -> 7.5 world units
    CHRONOS_BUBBLE_RADIUS: 15,      // 300 px -> 15 world units
    CHRONOS_BUBBLE_TIME_SCALE: 0.3,
    RESONANT_DROP_RANGE: 7.5,
  },

  TIME: {
    PHASE_DURATION: 8000,
    TRANSITION_DURATION: 2000,
    SHADOW_EXPAND_FACTOR: 3.0,
  },
} as const;

/**
 * Clockwork Canopy — Level 3 configuration.
 * Redwood peaks with clockwork observatories, giant glass lenses, star charts.
 * Theme: Integration and Harmony.
 */
export const CLOCKWORK_CANOPY = {
  /** World size in Babylon units. */
  WORLD_WIDTH: 140,
  WORLD_HEIGHT: 80,
  GROUND_DEPTH: 2,

  /** Canopy-specific colors (hex strings for Color3.FromHexString). */
  COLORS: {
    STARLIT_SKY: '#050818',
    MIDNIGHT_BLUE: '#0a1030',
    BRASS_WARM: '#c49b3c',
    GOLD_FOG: '#1a1508',
    COPPER_DARK: '#6b4020',
    LENS_RED: '#cc2244',
    LENS_GREEN: '#22cc66',
    LENS_BLUE: '#2266cc',
    LENS_GOLD: '#ccaa22',
    CONSTELLATION: '#aaccff',
    ORRERY_BRASS: '#b8963c',
  },

  PUZZLE: {
    /** Gear alignment — notches and target positions. */
    GEAR_NOTCHES_A: 8,
    GEAR_TARGET_A: 3,
    GEAR_NOTCHES_B: 8,
    GEAR_TARGET_B: 6,
    /** Light path — lens count. */
    LENS_COUNT: 3,
    /** Timed resonance — bubble window in seconds. */
    CHRONOS_WINDOW: 5.0,
    /** Co-op ability ranges. */
    RESONANT_DROP_RANGE: 7.5,
    RESONANT_DROP_RADIUS: 12.5,
    RESONANT_DROP_COOLDOWN: 3.0,
    CHRONOS_BUBBLE_RANGE: 7.5,
    CHRONOS_BUBBLE_RADIUS: 15,
    CHRONOS_BUBBLE_TIME_SCALE: 0.3,
  },

  NPC: {
    FOX_PATROL_SPEED: 5.0,
    FOX_ALERT_RADIUS: 8.0,
    FOX_CHASE_SPEED: 9.0,
  },
} as const;

/**
 * NPC tuning constants.
 * Ported from Phaser SCRAPYARD.NPC / OVERGROWN_CONSERVATORY.NPC.
 * Pixel speeds are converted: 1 Phaser px ~= 0.05 Babylon units.
 */
export const NPC_TUNING = {
  /** Default calm radius in world units. */
  CALM_RADIUS: 6.0,

  /** Kitten (Redwood Scrapyard) */
  KITTEN: {
    CALM_THRESHOLD: 100,
    CALM_RATE: 30,
    CALM_DECAY: 10,
    FOLLOW_SPEED: 7.0,      // 140 px/s -> 7.0
    FOLLOW_DISTANCE: 3.0,   // 60 px -> 3.0
  },

  /** Squirrel (Redwood Scrapyard) */
  SQUIRREL: {
    PATROL_SPEED: 4.0,      // 80 px/s -> 4.0
    DETECT_RADIUS: 6.0,     // 120 px -> 6.0
    // Agitated until Minna's Purr fills its calm meter (~3.3 s in range),
    // the same rates as a frantic kitten.
    CALM_THRESHOLD: 100,
    CALM_RATE: 30,
    CALM_DECAY: 10,
  },

  /** Fox (Midnight Burrow) */
  FOX: {
    PATROL_SPEED: 6.0,      // 120 px/s -> 6.0
    ALERT_RADIUS: 9.0,      // 180 px -> 9.0
    CHASE_SPEED: 10.0,      // 200 px/s -> 10.0
    CALM_THRESHOLD: 100,
  },

  /** Opossum (Midnight Burrow) */
  OPOSSUM: {
    FOLLOW_SPEED: 4.5,      // 90 px/s -> 4.5
    FOLLOW_DISTANCE: 2.5,   // 50 px -> 2.5
    SCATTER_SPEED: 8.0,     // 160 px/s -> 8.0
  },
} as const;

/**
 * Making Biscuits — Minna-only kneading mini-game on Elder anchor props.
 *
 * See `docs/specs/SPEC-making-biscuits.md`. Activation key is `b` so it
 * doesn't stomp Minna's Purr (which holds on `f`). The rhythm is an
 * alternating paw tap (`a` = left paw, `d` = right paw) with a generous
 * window, soft-fail, and an auto-knead assist mode for accessibility.
 *
 * All timings are intentionally forgiving — per design doc: *"No player
 * should miss lore because their timing is off."*
 */
export const BISCUITS = {
  /** Keyboard button that enters biscuit mode when Minna is near an anchor. */
  ACTIVATION_KEY: "b",
  /** Left paw button (alternating). */
  LEFT_PAW_KEY: "a",
  /** Right paw button (alternating). */
  RIGHT_PAW_KEY: "d",
  /** Proximity (world units) for the HUD prompt and activation. */
  INTERACT_RADIUS: 2.0,
  /** Trance depth target. Reach this to trigger the vision. */
  MAX_DEPTH: 100,
  /**
   * Per-beat fill (depth units) on a well-timed alternating tap. The
   * default 14 means ~8 well-timed alternating taps complete a session.
   */
  BEAT_FILL: 14,
  /** Reduced fill on a soft-miss (same paw twice, or out-of-window). */
  SOFT_MISS_FILL: 3,
  /** Depth decay (units/second) when the player isn't tapping. */
  DECAY_PER_SECOND: 6,
  /** Base window (seconds) between pulses. Multiplied by accessibility setting. */
  BASE_BEAT_WINDOW_SECONDS: 0.8,
  /**
   * How long a "just-fired" pulse stays on the good-hit window. The
   * actual hit window is `pulse period × HIT_WINDOW_FRACTION` centered
   * on the pulse. 0.6 is generous — 60% of every pulse is a good hit.
   */
  HIT_WINDOW_FRACTION: 0.6,
  /** Auto-knead assist: seconds to fully complete a biscuit session. */
  ASSIST_COMPLETE_SECONDS: 8,
} as const;

/**
 * Vision cutscene tuning — shared across all three Elder visions.
 * See `docs/specs/SPEC-making-biscuits.md` §4 Vision Delivery.
 */
export const VISION = {
  /** Sepia/desaturation entry fade duration (ms). */
  ENTER_FADE_MS: 1500,
  /** Exit fade duration (ms). */
  EXIT_FADE_MS: 1200,
  /** Per-beat dwell for whispered lines (ms). */
  BEAT_DURATION_MS: 4000,
  /** Replay visions use a shorter dwell to avoid replaying full prose. */
  REPLAY_BEAT_DURATION_MS: 2500,
  /** Post-process chromatic aberration strength during the body of the vision. */
  CHROMATIC_ABERRATION: 1.2,
  /** Sepia tint intensity [0..1]. */
  SEPIA_STRENGTH: 0.55,
  /** Exit toast text. */
  EXIT_TOAST: "A memory stirs.",
} as const;
